/**
 * Pure H OS · Phase D Wave 2 · Procurement.
 * Purchase order list across suppliers and categories.
 */
import { useEffect, useMemo, useState } from "react";
import { Card, SectionHeader, SeverityBadge, Button } from "../components/lib";
import { TabBar, ListRow, Drawer } from "../components/lib.overlay";
import { LinkedRefChip } from "../components/LinkedRefChip";
import { DocumentActions } from "../components/DocumentActions";
import { RecordEditor, FieldSpec } from "../components/RecordEditor";
import { AuditTrail } from "../components/AuditTrail";
import { PROCUREMENT, PoStatus, PurchaseOrder } from "../mocks/procurementData";
import { api } from "../services/api";
import { upsertLocal } from "../services/store";
import { useLiveTick } from "../lib/useLiveTick";

const STATUS_LABEL: Record<PoStatus, string> = {
  draft: "Draft", pending: "Pending", approved: "Approved",
  received: "Received", closed: "Closed",
};
function statusSeverity(s: PoStatus): "low" | "medium" | "high" {
  if (s === "pending" || s === "draft") return "medium";
  if (s === "closed" || s === "received" || s === "approved") return "low";
  return "high";
}

type Tab = "all" | PoStatus;

export default function Procurement() {
  const [tab, setTab] = useState<Tab>("all");
  const [openId, setOpenId] = useState<string | null>(null);
  const [busy, setBusy] = useState<PoStatus | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(false);
  useLiveTick();

  // Deep-link: /procurement?supplier=Foo&new=po|pr auto-opens the editor
  // with the supplier pre-filled.
  const urlParams = typeof window !== "undefined" ? new URLSearchParams(window.location.search) : null;
  const preSupplier = urlParams?.get("supplier") || "";
  const preMode     = urlParams?.get("new");
  useEffect(() => { if (preMode === "po" || preMode === "pr") setCreating(true); }, [preMode]);

  // Auto-generate a PO number when the user opens the create editor.
  const nextPoNumber = useMemo(() => {
    const yr = new Date().getFullYear();
    const seq = (PROCUREMENT.length + 1).toString().padStart(4, "0");
    const prefix = preMode === "pr" ? "PR" : "PO";
    return `${prefix}-${yr}-${seq}`;
  }, [preMode]);

  // Memoise initial payload so RecordEditor's effect doesn't churn.
  const createInitial = useMemo(
    () => ({
      supplier: preSupplier,
      category: "materials",
      vatPct: 14,
      poNumber: nextPoNumber,
      status: preMode === "pr" ? "draft" : "pending",
    }) as unknown as Partial<PurchaseOrder>,
    [preSupplier, preMode, nextPoNumber],
  );

  const poSchema: FieldSpec[] = [
    { key: "poNumber",        label: "PO / PR number", kind: "text",     placeholder: nextPoNumber },
    { key: "supplier",        label: "Supplier",       kind: "text",     placeholder: "e.g. Ambrox Naturals" },
    { key: "category",        label: "Category",       kind: "select",   options: [
      { value: "materials", label: "Materials" }, { value: "packaging", label: "Packaging" },
      { value: "services", label: "Services" }, { value: "logistics", label: "Logistics" },
      { value: "other", label: "Other" },
    ] },
    { key: "amountEGP",       label: "Amount (EGP)",   kind: "number",   placeholder: "125000" },
    { key: "vatPct",          label: "VAT %",          kind: "number",   placeholder: "14" },
    { key: "owner",           label: "Owner",          kind: "text",     placeholder: "e.g. Anas" },
    { key: "orderedRelative", label: "Ordered date",   kind: "text",     placeholder: "2026-07-05" },
    { key: "expectedRelative",label: "Expected date",  kind: "text",     placeholder: "2026-07-20" },
    { key: "notes",           label: "Notes",          kind: "textarea" },
  ];

  async function applyStatus(status: PoStatus) {
    if (!openId) return;
    setBusy(status); setErr(null);
    try {
      const updated = await api.patch("procurement", openId, { status }) as Partial<PurchaseOrder> & { id: string };
      upsertLocal("procurement", updated);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Update failed");
    } finally { setBusy(null); }
  }

  const filtered = useMemo(() => (tab === "all" ? PROCUREMENT : PROCUREMENT.filter((p) => p.status === tab)), [tab]);
  const current = openId ? PROCUREMENT.find((p) => p.id === openId) ?? null : null;

  return (
    <div className="space-y-6" data-testid="procurement-page">
      <Card testId="pr-filters">
        <div className="flex items-center justify-between gap-3">
          <TabBar<Tab>
            testId="pr-tabs"
            active={tab}
            onChange={setTab}
            tabs={[
              { key: "all",      label: `All ${PROCUREMENT.length}` },
              { key: "draft",    label: "Draft" },
              { key: "pending",  label: "Pending" },
              { key: "approved", label: "Approved" },
              { key: "received", label: "Received" },
              { key: "closed",   label: "Closed" },
            ]}
          />
          <Button testId="pr-new" onClick={() => setCreating(true)}>+ New PO</Button>
        </div>
      </Card>

      <Card padded={false} testId="pr-list">
        <div className="p-5" style={{ borderBottom: "1px solid var(--ph-border-subtle)" }}>
          <SectionHeader eyebrow="Purchase Orders" title={`${filtered.length} PO${filtered.length === 1 ? "" : "s"}`} />
          <DocumentActions docKey="purchase-order" testId="proc-doc-actions" />
          <div className="text-xs" style={{ color: "var(--ph-muted)" }}>Every PO across raw materials, packaging, and services.</div>
        </div>
        <div className="p-2">
          {filtered.map((p) => (
            <ListRow key={p.id} testId={`pr-${p.id}`}
              onClick={() => setOpenId(p.id)}
              label={
                <span className="flex items-center gap-2">
                  <span className="uppercase tracking-[0.14em] text-[10px]" style={{ color: "var(--ph-muted)" }}>{p.poNumber}</span>
                  <span>{p.supplier}</span>
                </span>
              }
              sublabel={<>{p.category} · {p.owner} · EGP {p.amountEGP.toLocaleString()} · {p.orderedRelative} → {p.expectedRelative}</>}
              trailing={<SeverityBadge severity={statusSeverity(p.status)} />}
            />
          ))}
        </div>
      </Card>

      <Drawer open={!!current} onClose={() => setOpenId(null)} testId="pr-drawer" title={current ? `${current.poNumber} · ${current.supplier}` : ""}>
        {current && (
          <div className="space-y-6">
            <SectionHeader eyebrow={`${STATUS_LABEL[current.status]} · ${current.category}`} title={current.supplier} />
            {/* Single-record PO / Goods Receipt PDF · ORVAN branded */}
            <div className="flex items-center gap-3 pb-3 flex-wrap" style={{ borderBottom: "1px solid var(--ph-border-subtle)" }}>
              <DocumentActions docKey="purchase-order" recordId={current.id} testId={`pr-single-po-${current.id}`} compact />
              <DocumentActions docKey="goods-receipt"  recordId={current.id} testId={`pr-single-gr-${current.id}`} compact />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-muted)" }}>Amount</div>
                <div className="mt-1 text-sm tabular-nums" style={{ color: "var(--ph-text)" }}>EGP {current.amountEGP.toLocaleString()}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-muted)" }}>Owner</div>
                <div className="mt-1 text-sm" style={{ color: "var(--ph-text)" }}>{current.owner}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-muted)" }}>Ordered</div>
                <div className="mt-1 text-sm" style={{ color: "var(--ph-text)" }}>{current.orderedRelative}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-muted)" }}>Expected</div>
                <div className="mt-1 text-sm" style={{ color: "var(--ph-text)" }}>{current.expectedRelative}</div>
              </div>
              {current.linkedTo && (
                <div className="col-span-2">
                  <div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-muted)" }}>Linked decision</div>
                  <div className="mt-1 text-sm" style={{ color: "var(--ph-text)" }}>
                    <LinkedRefChip id={current.linkedTo} kind="decision" testId={`pr-linked-${current.id}`} />
                  </div>
                </div>
              )}
            </div>

            <div className="pt-4" style={{ borderTop: "1px solid var(--ph-border-subtle)" }}>
              <div className="text-[10px] uppercase tracking-[0.16em] mb-2" style={{ color: "var(--ph-muted)" }}>
                PO actions · current <span className="font-semibold" style={{ color: "var(--ph-text)" }}>{STATUS_LABEL[current.status]}</span>
              </div>
              {err && <div className="text-xs mb-2" style={{ color: "var(--ph-danger)" }} data-testid="pr-action-error">{err}</div>}
              <div className="flex flex-wrap gap-2">
                <Button variant="secondary" disabled={busy !== null} onClick={() => setEditing(true)} testId="pr-action-edit">Edit</Button>
                <Button variant="primary"   disabled={busy !== null} onClick={() => applyStatus("pending")} testId="pr-action-pending">
                  {busy === "pending" ? "Submitting…" : "Submit for approval"}
                </Button>
                <Button variant="primary"   disabled={busy !== null} onClick={() => applyStatus("approved")} testId="pr-action-approve">
                  {busy === "approved" ? "Approving…" : "Approve"}
                </Button>
                <Button variant="secondary" disabled={busy !== null} onClick={() => applyStatus("received")} testId="pr-action-received">
                  {busy === "received" ? "Marking…" : "Mark received"}
                </Button>
                <Button variant="secondary" disabled={busy !== null} onClick={() => applyStatus("closed")} testId="pr-action-closed">
                  {busy === "closed" ? "Closing…" : "Close"}
                </Button>
              </div>
              <AuditTrail collection="procurement" recordId={current.id} testId="pr-audit" />
            </div>
          </div>
        )}
      </Drawer>

      <RecordEditor<PurchaseOrder>
        open={creating}
        onClose={() => setCreating(false)}
        mode="create"
        title={preMode === "pr" ? "New purchase request" : "New purchase order"}
        collection="procurement"
        schema={poSchema}
        initial={createInitial}
        sendForApprovalStatus="pending"
        testId="pr-new-drawer"
      />

      {current && (
        <RecordEditor<PurchaseOrder>
          open={editing}
          onClose={() => setEditing(false)}
          mode="edit"
          title={`Edit ${current.poNumber}`}
          collection="procurement"
          schema={poSchema}
          initial={current as unknown as Partial<PurchaseOrder>}
          onSaved={(row) => upsertLocal("procurement", row as { id?: string })}
          testId="pr-edit-drawer"
        />
      )}
    </div>
  );
}
